(function() {
  const SELECTOR = '[data-fermentation-manual]';

  class FermentationManualNav {
    constructor(root) {
      this.root = root;
      this.nav = root.querySelector('[data-manual-nav]');
      this.track = root.querySelector('[data-manual-nav-track]');
      this.indicator = root.querySelector('[data-manual-nav-indicator]');
      this.sentinel = root.querySelector('[data-manual-nav-sentinel]');
      this.headerSection = document.querySelector('.shopify-section-header');
      this.headerElement = this.headerSection ? this.headerSection.querySelector('.header') : null;
      this.links = Array.from(root.querySelectorAll('[data-manual-nav-link]'));
      this.sections = this.links
        .map((link) => {
          const id = link.getAttribute('href');
          if (!id || !id.startsWith('#')) return null;
          const section = root.querySelector(id);
          return section ? { id, link, section } : null;
        })
        .filter(Boolean);

      this.activeId = null;
      this.pendingTargetId = null;
      this.pendingTargetTop = null;
      this.rafId = null;
      this.resizeRaf = null;
      this.scrollAnimationFrame = null;
      this.indicatorResetFrame = null;
      this.lastHeaderOffset = null;
      this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      if (!this.nav || !this.track || !this.indicator || !this.sentinel || this.sections.length === 0) {
        return;
      }

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onTrackClick = this.onTrackClick.bind(this);
      this.onHeaderResize = this.onHeaderResize.bind(this);
      this.onUserScrollInterrupt = this.onUserScrollInterrupt.bind(this);

      this.init();
    }

    init() {
      this.updateMetrics();
      this.updateStickyState();
      this.updateActiveSection();

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });
      window.addEventListener('wheel', this.onUserScrollInterrupt, { passive: true });
      window.addEventListener('touchstart', this.onUserScrollInterrupt, { passive: true });
      this.track.addEventListener('click', this.onTrackClick);

      if ('ResizeObserver' in window && this.headerSection) {
        this.headerResizeObserver = new ResizeObserver(this.onHeaderResize);
        this.headerResizeObserver.observe(this.headerSection);
      } else if ('ResizeObserver' in window && this.headerElement) {
        this.headerResizeObserver = new ResizeObserver(this.onHeaderResize);
        this.headerResizeObserver.observe(this.headerElement);
      }

      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => {
          this.updateMetrics();
          this.updateIndicator(false);
        });
      }
    }

    getHeaderOffset() {
      if (this.headerSection) {
        const sectionRect = this.headerSection.getBoundingClientRect();

        if (sectionRect.height > 0) {
          return Math.max(0, Math.round(sectionRect.bottom));
        }
      }

      const header = this.headerElement;

      if (!header) {
        return 0;
      }

      return Math.max(0, Math.round(header.getBoundingClientRect().bottom));
    }

    getScrollOffset() {
      const navHeight = this.nav ? this.nav.getBoundingClientRect().height : 0;
      return this.getHeaderOffset() + navHeight + 8;
    }

    getMaxScrollTop() {
      const scrollingElement = document.scrollingElement || document.documentElement;
      return Math.max(0, scrollingElement.scrollHeight - window.innerHeight);
    }

    getTargetScrollTop(target) {
      const rawTop = window.scrollY + target.getBoundingClientRect().top - this.getScrollOffset();
      return Math.max(0, Math.min(rawTop, this.getMaxScrollTop()));
    }

    updateMetrics() {
      const headerOffset = this.getHeaderOffset();
      const scrollOffset = headerOffset + (this.nav ? this.nav.getBoundingClientRect().height : 0) + 8;

      if (headerOffset !== this.lastHeaderOffset) {
        this.lastHeaderOffset = headerOffset;
      }

      this.root.style.setProperty('--fm-sticky-offset', `${headerOffset}px`);
      this.root.style.setProperty('--fm-scroll-offset', `${scrollOffset}px`);
    }

    onScroll() {
      if (this.rafId) return;
      this.rafId = window.requestAnimationFrame(() => {
        this.updateMetrics();
        this.updateStickyState();
        this.updateActiveSection();
        this.rafId = null;
      });
    }

    onResize() {
      if (this.resizeRaf) return;
      this.resizeRaf = window.requestAnimationFrame(() => {
        this.updateMetrics();
        this.updateStickyState();
        this.updateActiveSection();
        this.resizeRaf = null;
      });
    }

    onHeaderResize() {
      this.updateMetrics();
      this.updateStickyState();
      this.updateActiveSection();
    }

    onUserScrollInterrupt() {
      if (!this.scrollAnimationFrame) return;

      this.cancelScrollAnimation();
      this.clearPendingTarget();
      this.updateActiveSection();
    }

    onTrackClick(event) {
      const link = event.target.closest('[data-manual-nav-link]');
      if (!link) return;

      const targetId = link.getAttribute('href');
      const target = targetId ? this.root.querySelector(targetId) : null;
      if (!target) return;

      event.preventDefault();
      const targetTop = this.getTargetScrollTop(target);

      this.cancelScrollAnimation();
      this.setActiveLink(targetId, true);
      this.holdPendingTarget(targetId, targetTop);
      this.scrollToTarget(targetTop);
    }

    updateStickyState() {
      const stickyOffset = this.getHeaderOffset();
      const sentinelTop = this.sentinel.getBoundingClientRect().top;
      const isStuck = sentinelTop <= stickyOffset;
      this.nav.classList.toggle('is-stuck', isStuck);
    }

    updateActiveSection() {
      const activationLine = this.getScrollOffset();

      if (this.pendingTargetId) {
        const pendingTarget = this.sections.find((item) => item.id === this.pendingTargetId);

        if (pendingTarget) {
          const pendingRect = pendingTarget.section.getBoundingClientRect();
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          const maxScrollTop = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
          const reachedTarget =
            Math.abs(pendingRect.top - activationLine) <= 12 ||
            (this.pendingTargetTop !== null && Math.abs(scrollTop - this.pendingTargetTop) <= 6) ||
            (scrollTop >= maxScrollTop - 2 && pendingRect.top - activationLine <= 12);

          if (!reachedTarget) {
            this.setActiveLink(this.pendingTargetId, false);
            return;
          }
        }

        this.clearPendingTarget();
      }

      let current = this.sections[0];

      this.sections.forEach((item) => {
        const rect = item.section.getBoundingClientRect();
        if (rect.top - activationLine <= 0) {
          current = item;
        }
      });

      if (current) {
        this.setActiveLink(current.id, false);
      }
    }

    holdPendingTarget(id, top) {
      this.pendingTargetId = id;
      this.pendingTargetTop = top;
    }

    clearPendingTarget() {
      this.pendingTargetId = null;
      this.pendingTargetTop = null;
    }

    cancelScrollAnimation() {
      if (!this.scrollAnimationFrame) return;

      window.cancelAnimationFrame(this.scrollAnimationFrame);
      this.scrollAnimationFrame = null;
    }

    finishTargetScroll() {
      this.cancelScrollAnimation();
      this.updateMetrics();
      this.updateStickyState();
      this.updateActiveSection();
    }

    scrollToTarget(targetTop) {
      if (this.prefersReducedMotion.matches) {
        window.scrollTo(0, targetTop);
        this.finishTargetScroll();
        return;
      }

      const startTop = window.pageYOffset || document.documentElement.scrollTop;
      const distance = targetTop - startTop;

      if (Math.abs(distance) < 2) {
        window.scrollTo(0, targetTop);
        this.finishTargetScroll();
        return;
      }

      const duration = Math.max(420, Math.min(760, Math.abs(distance) * 0.28));
      const startTime = window.performance.now();

      const step = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const eased =
          progress < 0.5
            ? 4 * progress * progress * progress
            : 1 - Math.pow(-2 * progress + 2, 3) / 2;

        window.scrollTo(0, Math.round(startTop + (distance * eased)));

        if (progress < 1) {
          this.scrollAnimationFrame = window.requestAnimationFrame(step);
          return;
        }

        window.scrollTo(0, targetTop);
        this.finishTargetScroll();
      };

      this.scrollAnimationFrame = window.requestAnimationFrame(step);
    }

    shouldAnimateTrackOnClick() {
      return window.innerWidth < 750 && !this.prefersReducedMotion.matches;
    }

    setActiveLink(id, fromClick) {
      if (!id || this.activeId === id) {
        if (fromClick) {
          this.centerActiveLink(this.shouldAnimateTrackOnClick());
        }
        return;
      }

      const hadActiveLink = Boolean(this.activeId);
      this.activeId = id;

      this.sections.forEach((item) => {
        const isActive = item.id === id;
        item.link.classList.toggle('is-active', isActive);
        if (isActive) {
          item.link.setAttribute('aria-current', 'true');
        } else {
          item.link.removeAttribute('aria-current');
        }
      });

      this.updateIndicator(hadActiveLink);
      this.centerActiveLink(fromClick ? this.shouldAnimateTrackOnClick() : true);
    }

    updateIndicator(animate) {
      const activeLink = this.track.querySelector('.is-active');
      if (!activeLink) {
        this.indicator.style.opacity = '0';
        return;
      }

      if (this.indicatorResetFrame) {
        window.cancelAnimationFrame(this.indicatorResetFrame);
        this.indicatorResetFrame = null;
      }

      const trackRect = this.track.getBoundingClientRect();
      const linkRect = activeLink.getBoundingClientRect();
      const left = linkRect.left - trackRect.left + this.track.scrollLeft;

      if (!animate) {
        this.indicator.style.transition = 'none';
      } else {
        this.indicator.style.removeProperty('transition');
      }

      this.indicator.style.opacity = '1';
      this.indicator.style.width = `${linkRect.width}px`;
      this.indicator.style.transform = `translate3d(${left}px, 0, 0)`;

      if (!animate) {
        this.indicatorResetFrame = window.requestAnimationFrame(() => {
          this.indicator.style.removeProperty('transition');
          this.indicatorResetFrame = null;
        });
      }
    }

    centerActiveLink(animate) {
      const activeLink = this.track.querySelector('.is-active');
      if (!activeLink) return;

      const targetLeft = activeLink.offsetLeft - (this.track.clientWidth / 2) + (activeLink.clientWidth / 2);
      const maxLeft = this.track.scrollWidth - this.track.clientWidth;
      const nextLeft = Math.max(0, Math.min(targetLeft, maxLeft));

      this.track.scrollTo({
        left: nextLeft,
        behavior: animate && !this.prefersReducedMotion.matches ? 'smooth' : 'auto'
      });
    }
  }

  const init = () => {
    document.querySelectorAll(SELECTOR).forEach((root) => {
      if (root.dataset.manualNavReady === 'true') return;
      root.dataset.manualNavReady = 'true';
      new FermentationManualNav(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
